import React from "react";
import { t } from "i18next";
import { getElementName } from "qpr-base/tplutils/utils";
import useLink from "qpr-base/hooks/useLink";
import typeToView from "routing/typetoview";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";

type ScorecardRow = {
  id: string;
  typename: string;
  index: number;
  topParentId: string;
  topParentTypename: string;
  topParentName: string;
  topParentSortKey: string;
  perspectiveId: string;
  perspectiveTypename: string;
  perspectiveName: string;
  perspectiveSortKey: string;
  elementSymbol: string;
  kpiName: string;
  weight: string;
  targetYearly: string;
  targetQ1: string;
  actuals: string;
  perfVsActual: string;
  q1ScoreText: string;
  q1ScoreNumeric: number | null;
};

type Props = {
  elements: any[];
  language: string;
  corporateScore?: string;
};

const headerCellStyle: React.CSSProperties = {
  background: "linear-gradient(180deg, #102a7a 0%, #0b1f63 100%)",
  color: "#fff",
  fontWeight: 700,
  fontSize: "1.14rem",
  letterSpacing: "0.2px",
  padding: "11px 13px",
  height: 46,
  border: "1px solid #3e5bb0",
};

const groupedHeaderStyle: React.CSSProperties = {
  borderLeft: "1px solid #5f7ad1",
  borderRight: "1px solid #5f7ad1",
  borderBottom: "1px solid #5f7ad1",
  boxShadow: "inset 0 -1px 0 #6f8cdf",
};

const topRowHeaderStyle: React.CSSProperties = {
  top: 0,
  zIndex: 5,
};

const secondRowHeaderStyle: React.CSSProperties = {
  top: 46,
  zIndex: 4,
  borderTop: "1px solid #6f8cdf",
};

const bodyCellStyle: React.CSSProperties = {
  fontSize: "1.1rem",
  padding: "10px 13px",
  color: "#0f172a",
};

const clickableTextStyle: React.CSSProperties = {
  cursor: "pointer",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  textDecorationThickness: "1px",
  color: "#132f88",
  fontWeight: 600,
  fontSize: "1.12rem",
};

const OverviewScorecardTable: React.FC<Props> = ({ elements, language, corporateScore }) => {
  const link = useLink();

  const goToElement = React.useCallback(
    (id?: string, typename?: string) => {
      if (!id || !typename) return;
      const page = typeToView(typename);
      if (!page) return;
      link({ page, meaId: id });
    },
    [link]
  );

  const scorecardRows = React.useMemo<ScorecardRow[]>(() => {
    const byId = new Map(elements.map((el) => [el.id, el] as const));
    const rows = elements.filter(
      (el) => el.typename === "KPI" && el.STATUS === "1"
    );
    return rows.map((el, idx) => {
      const parentId = (el as any).parent_element_id || (el as any).parentelementid;
      const parentChain: {
        id: string;
        name: string;
        symbol: string;
        typename: string;
      }[] = [];
      const visited = new Set<string>();
      let cursor = parentId ? byId.get(parentId) : undefined;
      while (cursor && !visited.has(cursor.id)) {
        visited.add(cursor.id);
        if (cursor.typename !== "Organization") {
          const localizedName = getElementName(cursor, language);
          parentChain.push({
            id: cursor.id || "",
            name: localizedName || cursor.name || "-",
            symbol: cursor.symbol || "",
            typename: cursor.typename || "",
          });
        }
        const nextId =
          (cursor as any).parent_element_id || (cursor as any).parentelementid;
        cursor = nextId ? byId.get(nextId) : undefined;
      }
      parentChain.reverse();
      const perspectiveNode = [...parentChain]
        .reverse()
        .find((p) => p.typename === "Perspective");
      const parentAbovePerspective = perspectiveNode
        ? parentChain.find(
            (p) =>
              p.typename !== "Perspective" &&
              p.symbol !== perspectiveNode.symbol
          )
        : undefined;
      const fallbackParent = parentChain.find((p) => p.typename !== "Perspective");

      const score =
        el.SCORE_YTD_prettyvalue ||
        el.SCORE_prettyvalue ||
        el.SCORE_YTD_valuewithoutsymbol ||
        "--";
      const numericScore = Number(String(score).replace(/[^0-9.-]/g, ""));
      return {
        id: el.id,
        typename: el.typename || "KPI",
        index: idx + 1,
        topParentId: parentAbovePerspective?.id || fallbackParent?.id || "",
        topParentTypename:
          parentAbovePerspective?.typename || fallbackParent?.typename || "",
        topParentName: parentAbovePerspective?.name || fallbackParent?.name || "-",
        topParentSortKey:
          parentAbovePerspective?.symbol || fallbackParent?.symbol || fallbackParent?.name || "-",
        perspectiveId: perspectiveNode?.id || "",
        perspectiveTypename: perspectiveNode?.typename || "",
        perspectiveName: perspectiveNode?.name || "-",
        perspectiveSortKey: perspectiveNode?.symbol || "-",
        elementSymbol: el.symbol || "",
        kpiName: getElementName(el, language) || el.name || "-",
        weight: el.W_prettyvalue || el.W || "--",
        targetYearly: el.TAR_YTD_prettyvalue || el.TAR_prettyvalue || el.TAR || "--",
        targetQ1: el.TAR_prettyvalue || el.TAR || "--",
        actuals: el.ACT_prettyvalue || el.ACT || "--",
        perfVsActual: el.PERF_prettyvalue || el.PERF_YTD_prettyvalue || el.PERF || "--",
        q1ScoreText: score,
        q1ScoreNumeric: Number.isFinite(numericScore) ? numericScore : null,
      };
    });
  }, [elements, language]);

  const groupedRows = React.useMemo(() => {
    type SecondColumnGroup = {
      secondId: string;
      secondTypename: string;
      secondValue: string;
      secondSortKey: string;
      rows: ScorecardRow[];
      average: number | null;
    };
    type FirstColumnGroup = {
      firstId: string;
      firstTypename: string;
      firstValue: string;
      firstSortKey: string;
      secondGroups: SecondColumnGroup[];
      rowCount: number;
      average: number | null;
    };

    const byFirst = new Map<string, Map<string, ScorecardRow[]>>();
    scorecardRows.forEach((row) => {
      // First column currently shows perspective values, second shows parent values.
      const firstKey = `${row.perspectiveSortKey}||${row.perspectiveName}||${row.perspectiveId}||${row.perspectiveTypename}`;
      const secondKey = `${row.topParentSortKey}||${row.topParentName}||${row.topParentId}||${row.topParentTypename}`;
      if (!byFirst.has(firstKey)) byFirst.set(firstKey, new Map<string, ScorecardRow[]>());
      const secondMap = byFirst.get(firstKey)!;
      if (!secondMap.has(secondKey)) secondMap.set(secondKey, []);
      secondMap.get(secondKey)!.push(row);
    });

    const out: FirstColumnGroup[] = [];
    byFirst.forEach((secondMap, firstKey) => {
      const [firstSortKey, firstValue, firstId, firstTypename] =
        firstKey.split("||");
      const secondGroups: SecondColumnGroup[] = [];
      secondMap.forEach((rows, secondKey) => {
        rows.sort((a, b) => a.elementSymbol.localeCompare(b.elementSymbol));
        const nums = rows
          .map((r) => r.q1ScoreNumeric)
          .filter((n: number | null): n is number => typeof n === "number");
        const average = nums.length
          ? nums.reduce((sum: number, n: number) => sum + n, 0) / nums.length
          : null;
        const [secondSortKey, secondValue, secondId, secondTypename] =
          secondKey.split("||");
        secondGroups.push({
          secondValue,
          secondSortKey,
          secondId,
          secondTypename,
          rows,
          average,
        });
      });
      secondGroups.sort((a, b) => a.secondSortKey.localeCompare(b.secondSortKey));
      const rowCount = secondGroups.reduce((sum, g) => sum + g.rows.length, 0);
      const groupNums = secondGroups
        .flatMap((g) => g.rows)
        .map((r) => r.q1ScoreNumeric)
        .filter((n: number | null): n is number => typeof n === "number");
      const average = groupNums.length
        ? groupNums.reduce((sum: number, n: number) => sum + n, 0) / groupNums.length
        : null;
      out.push({
        firstValue,
        firstSortKey,
        firstId,
        firstTypename,
        secondGroups,
        rowCount,
        average,
      });
    });
    out.sort((a, b) => a.firstSortKey.localeCompare(b.firstSortKey));
    return out;
  }, [scorecardRows]);

  const totalRowCount = React.useMemo(
    () => groupedRows.reduce((sum, g) => sum + g.rowCount, 0),
    [groupedRows]
  );

  const corporateScoreNumeric = corporateScore
    ? Number(String(corporateScore).replace(/[^0-9.-]/g, ""))
    : null;
  const corporateScoreColor =
    corporateScoreNumeric == null || !Number.isFinite(corporateScoreNumeric)
      ? "#1a1a1a"
      : corporateScoreNumeric >= 3
      ? "#00a651"
      : "#ff0000";

  return (
    <TableContainer
      component={Paper}
      style={{
        border: "2px solid #203d99",
        marginBottom: "20px",
        maxHeight: "calc(100vh - 180px)",
        borderRadius: "10px",
        boxShadow: "0 12px 28px rgba(11, 31, 99, 0.2)",
        background: "#ffffff",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2} style={{ ...headerCellStyle, ...topRowHeaderStyle }}>
              {t("overview.Parent")}
            </TableCell>
            <TableCell rowSpan={2} style={{ ...headerCellStyle, ...topRowHeaderStyle }}>
              {t("overview.Perspective")}
            </TableCell>
            <TableCell rowSpan={2} style={{ ...headerCellStyle, ...topRowHeaderStyle }}>
              #
            </TableCell>
            <TableCell rowSpan={2} style={{ ...headerCellStyle, ...topRowHeaderStyle }}>
              {t("overview.Key Performance Indicators")}
            </TableCell>
            <TableCell rowSpan={2} style={{ ...headerCellStyle, ...topRowHeaderStyle }}>
              {t("overview.Weights")}
            </TableCell>
            <TableCell
              colSpan={2}
              align="center"
              style={{
                ...headerCellStyle,
                ...groupedHeaderStyle,
                ...topRowHeaderStyle,
              }}
            >
              {t("overview.Target")}
            </TableCell>
            <TableCell rowSpan={2} style={{ ...headerCellStyle, ...topRowHeaderStyle }}>
              {t("overview.Actuals")}
            </TableCell>
            <TableCell rowSpan={2} style={{ ...headerCellStyle, ...topRowHeaderStyle }}>
              {t("overview.Performance vs Actual")}
            </TableCell>
            <TableCell
              colSpan={3}
              align="center"
              style={{
                ...headerCellStyle,
                ...groupedHeaderStyle,
                ...topRowHeaderStyle,
              }}
            >
              {t("overview.Q1")}
            </TableCell>
            <TableCell
              rowSpan={2}
              align="center"
              style={{
                ...headerCellStyle,
                ...topRowHeaderStyle,
                background: "linear-gradient(180deg, #1a3a6e 0%, #0f2550 100%)",
                borderLeft: "2px solid #6f8cdf",
              }}
            >
              {t("overview.Total Score")}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="center" style={{ ...headerCellStyle, ...secondRowHeaderStyle }}>
              {t("overview.Yearly")}
            </TableCell>
            <TableCell align="center" style={{ ...headerCellStyle, ...secondRowHeaderStyle }}>
              {t("overview.Q1")}
            </TableCell>
            <TableCell align="center" style={{ ...headerCellStyle, ...secondRowHeaderStyle }}>
              {t("overview.Score out of 5")}
            </TableCell>
            <TableCell align="center" style={{ ...headerCellStyle, ...secondRowHeaderStyle }}>
              {t("overview.Perspective Score")}
            </TableCell>
            <TableCell align="center" style={{ ...headerCellStyle, ...secondRowHeaderStyle }}>
              {t("overview.Parent Score")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupedRows.map((group, groupIdx) =>
            group.secondGroups.map((secondGroup, secondIdx) =>
              secondGroup.rows.map((row, rowIdx) => {
                const scoreColor =
                  row.q1ScoreNumeric == null
                    ? "#1a1a1a"
                    : row.q1ScoreNumeric >= 3
                    ? "#00a651"
                    : "#ff0000";
                const rowBackground = row.index % 2 === 0 ? "#e7ecff" : "#dbe4ff";
                return (
                  <TableRow key={row.id} hover>
                    {secondIdx === 0 && rowIdx === 0 ? (
                      <TableCell
                        rowSpan={group.rowCount}
                        style={{
                          ...bodyCellStyle,
                          background: "#cfd9ff",
                          color: "#132b7a",
                          fontWeight: 700,
                          border: "1px solid #aebef5",
                        }}
                      >
                        <span
                          onClick={() =>
                            goToElement(group.firstId, group.firstTypename)
                          }
                        >
                          {group.firstValue}
                        </span>
                      </TableCell>
                    ) : null}
                    {rowIdx === 0 ? (
                      <TableCell
                        rowSpan={secondGroup.rows.length}
                        style={{
                          ...bodyCellStyle,
                          background: "#d9e2ff",
                          color: "#183687",
                          fontWeight: 700,
                          border: "1px solid #b9c7f5",
                        }}
                      >
                        <span
                          style={clickableTextStyle}
                          onClick={() =>
                            goToElement(
                              secondGroup.secondId,
                              secondGroup.secondTypename
                            )
                          }
                        >
                          {secondGroup.secondValue}
                        </span>
                      </TableCell>
                    ) : null}
                    <TableCell style={{ ...bodyCellStyle, background: rowBackground, border: "1px solid #b9c6ef", textAlign: "center", fontWeight: 600 }}>
                      {row.index}
                    </TableCell>
                    <TableCell
                      style={{
                        ...bodyCellStyle,
                        background: rowIdx === 0 ? "#ffe8a3" : rowBackground,
                        border: "1px solid #b9c6ef",
                      }}
                    >
                      <span
                        style={clickableTextStyle}
                        onClick={() => goToElement(row.id, row.typename)}
                      >
                        {row.kpiName}
                      </span>
                    </TableCell>
                    <TableCell style={{ ...bodyCellStyle, background: rowBackground, border: "1px solid #b9c6ef", textAlign: "center" }}>
                      {row.weight}
                    </TableCell>
                    <TableCell style={{ ...bodyCellStyle, background: rowBackground, border: "1px solid #b9c6ef", textAlign: "center" }}>
                      {row.targetYearly}
                    </TableCell>
                    <TableCell style={{ ...bodyCellStyle, background: rowBackground, border: "1px solid #b9c6ef", textAlign: "center" }}>
                      {row.targetQ1}
                    </TableCell>
                    <TableCell style={{ ...bodyCellStyle, background: rowBackground, border: "1px solid #b9c6ef", textAlign: "center" }}>
                      {row.actuals}
                    </TableCell>
                    <TableCell style={{ ...bodyCellStyle, background: rowBackground, border: "1px solid #b9c6ef", textAlign: "center" }}>
                      {row.perfVsActual}
                    </TableCell>
                    <TableCell
                      style={{
                        ...bodyCellStyle,
                        background: rowBackground,
                        border: "1px solid #b9c6ef",
                        color: scoreColor,
                        fontWeight: 700,
                        textAlign: "center",
                      }}
                    >
                      {row.q1ScoreText}
                    </TableCell>
                    {rowIdx === 0 ? (
                      <TableCell
                        rowSpan={secondGroup.rows.length}
                        style={{
                          ...bodyCellStyle,
                          background: rowBackground,
                          border: "1px solid #b9c6ef",
                          color:
                            secondGroup.average == null
                              ? "#1a1a1a"
                              : secondGroup.average >= 3
                              ? "#00a651"
                              : "#ff0000",
                          fontWeight: 700,
                          textAlign: "center",
                        }}
                      >
                        {secondGroup.average == null ? "--" : secondGroup.average.toFixed(2)}
                      </TableCell>
                    ) : null}
                    {secondIdx === 0 && rowIdx === 0 ? (
                      <TableCell
                        rowSpan={group.rowCount}
                        style={{
                          ...bodyCellStyle,
                          background: "#cad5ff",
                          border: "1px solid #afbef4",
                          color:
                            group.average == null
                              ? "#1a1a1a"
                              : group.average >= 3
                              ? "#00a651"
                              : "#ff0000",
                          fontWeight: 700,
                          textAlign: "center",
                        }}
                      >
                        {group.average == null ? "--" : group.average.toFixed(2)}
                      </TableCell>
                    ) : null}
                    {groupIdx === 0 && secondIdx === 0 && rowIdx === 0 ? (
                      <TableCell
                        rowSpan={totalRowCount}
                        align="center"
                        style={{
                          ...bodyCellStyle,
                          background: "#e8eeff",
                          borderLeft: "2px solid #6f8cdf",
                          border: "1px solid #afbef4",
                          color: corporateScoreColor,
                          fontWeight: 800,
                          fontSize: "1.3rem",
                          verticalAlign: "middle",
                        }}
                      >
                        {corporateScore || "--"}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OverviewScorecardTable;
